import { AnalyticCategories } from '@application/analytic/analytic.service';
import { AnalyticProvider, useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { Button, ButtonArrange, NotificationAlert, SafeArea, Stack, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Article } from '@component/article.component';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { usePresentation } from '@visiba/presentation';
import { useEffect, useState } from 'react';
import { PaymentContextProvider, usePaymentContext } from './_payment.context';
import { PaymentStateConveyer } from './_payment_state_conveyer.component';
import { AttemptData } from './payment.controller';

interface Props {
	billId: number;
	paymentAmount: string;
	issueTrackingId?: string;
	onSuccess?: (data: AttemptData) => void;
	onAbort?: () => void;
	asDialogContent?: boolean;
}

export function Payment(props: Props): JSX.Element {
	return (
		<AnalyticProvider category={AnalyticCategories.PaymentFlow}>
			<PaymentContextProvider>
				<PaymentInner {...props} />
			</PaymentContextProvider>
		</AnalyticProvider>
	);
}

function PaymentInner({ paymentAmount, billId, issueTrackingId, onSuccess, onAbort, asDialogContent = false }: Props): JSX.Element {
	const { controller } = usePaymentContext();
	const analyticCtx = useAnalyticContext();
	const [showCancel, setShowCancel] = useState(true);
	const [isClosing, setIsClosing] = useState(false);

	const paymentState = usePresentation(controller.state);

	useEffect(
		function bindEventsDuringLifeTime() {
			return controller.events.on('onSuccess', (data) => {
				setShowCancel(false);
				onSuccess?.(data);
			});
		},
		[controller, onSuccess],
	);

	useEffect(
		function bindEventsDuringLifeTime() {
			return controller.events.on('onAbort', () => {
				onAbort?.();
			});
		},
		[controller, onAbort],
	);

	useEffect(
		function whenComponentMountsOrBillIdChanges() {
			controller.loadBill(billId, issueTrackingId);
		},
		[controller, billId, issueTrackingId],
	);

	useEffect(
		function whenComponentUnmounts() {
			return () => {
				controller.tearDown();
			};
		},
		[controller],
	);

	function handleAbort(): void {
		analyticCtx.trackEvent('Clicked cancel');
		controller.cancel();
	}

	function handleClose(): void {
		setIsClosing(true);
		analyticCtx.trackEvent('Clicked close');
		controller.alreadyPaid();
	}

	return (
		<SafeArea maxWidth='560px'>
			<Stack gap={tokens.spacing.x4} fluid='horizontal'>
				<Stack gap={tokens.spacing.x2} fluid='horizontal'>
					{asDialogContent ? (
						<DialogPrimitive.Title asChild>
							<Text variant='headingMedium'>{withTranslation('PW.Component.Payment.Title')}</Text>
						</DialogPrimitive.Title>
					) : (
						<Text variant='headingMedium'>{withTranslation('PW.Component.Payment.Title')}</Text>
					)}
					{asDialogContent ? (
						<DialogPrimitive.Description asChild>
							<Text variant='bodyLargeRegular'>
								{withTranslation('PW.Component.Payment.Amount', {
									paymentAmount: paymentAmount,
								})}
							</Text>
						</DialogPrimitive.Description>
					) : (
						<Text variant='bodyLargeRegular'>
							{withTranslation('PW.Component.Payment.Amount', {
								paymentAmount: paymentAmount,
							})}
						</Text>
					)}
				</Stack>

				{paymentState.state === 'alreadyPaid' ? (
					<>
						<NotificationAlert variant='info'>
							<div>
								<Text variant='bodySemiBold'>{withTranslation('PW.Component.Payment.AlreadyPaid.Title')}</Text>
								<Article>{withTranslation('PW.Component.Payment.AlreadyPaid.Description')}</Article>
							</div>
						</NotificationAlert>

						<ButtonArrange>
							<Button variant='primary' size='small' onClick={handleClose} loading={isClosing}>
								{withTranslation('Common.Button.Close')}
							</Button>
						</ButtonArrange>
					</>
				) : (
					<>
						<PaymentStateConveyer />

						<ButtonArrange>
							<Button variant='ghost' size='small' onClick={handleAbort} disabled={!showCancel} disabledText=''>
								{withTranslation('PW.Component.Payment.ButtonCancel')}
							</Button>
						</ButtonArrange>
					</>
				)}
			</Stack>
		</SafeArea>
	);
}
